<template>
  <div class="org-settings">
    <header class="page-header">
      <div class="header-text">
        <h1 class="page-title">Organization</h1>
        <p class="page-subtitle">
          Profile and org-wide settings for
          <strong>{{ organization.name || 'your organization' }}</strong>
          — contact info, compliance, and email branding.
        </p>
      </div>
    </header>

    <div v-if="loading && !organization.name" class="loading-state">
      <i class="fas fa-spinner fa-spin"></i>
      Loading organization…
    </div>

    <template v-else>
      <div class="org-layout">
        <!-- Profile form -->
        <section class="settings-card">
          <div class="card-header">
            <i class="fas fa-building" aria-hidden="true"></i>
            <div>
              <h2 class="card-title">Organization profile</h2>
              <p class="card-desc">Legal name, industry, and primary contact shown on invoices and client communications.</p>
            </div>
          </div>

          <form @submit.prevent="updateOrganization" class="org-form">
            <div class="form-row">
              <div class="form-group">
                <label for="org-name">Organization name <span class="required">*</span></label>
                <input
                  id="org-name"
                  v-model="organization.name"
                  class="form-input"
                  required
                  placeholder="Your organization name"
                >
              </div>
              <div class="form-group">
                <label for="org-industry">Industry</label>
                <input
                  id="org-industry"
                  v-model="organization.industry"
                  class="form-input"
                  placeholder="e.g. Financial Services"
                >
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label for="org-email">Primary contact email <span class="required">*</span></label>
                <input
                  id="org-email"
                  v-model="organization.primary_contact_email"
                  type="email"
                  class="form-input"
                  required
                  placeholder="contact@yourorganization.com"
                >
              </div>
              <div class="form-group">
                <label for="org-phone">Primary contact phone</label>
                <input
                  id="org-phone"
                  v-model="organization.primary_contact_phone"
                  class="form-input"
                  placeholder="+1 (555) 123-4567"
                >
              </div>
            </div>

            <div class="form-group">
              <label for="org-desc">Description</label>
              <textarea
                id="org-desc"
                v-model="organization.description"
                class="form-input"
                rows="3"
                placeholder="Brief description of your organization…"
              ></textarea>
            </div>

            <fieldset class="address-fieldset">
              <legend>Address</legend>
              <div class="form-group">
                <label for="addr1">Address line 1</label>
                <input id="addr1" v-model="organization.address_line1" class="form-input" placeholder="123 Main Street">
              </div>
              <div class="form-group">
                <label for="addr2">Address line 2</label>
                <input id="addr2" v-model="organization.address_line2" class="form-input" placeholder="Suite 100">
              </div>
              <div class="form-row form-row--3">
                <div class="form-group">
                  <label for="city">City</label>
                  <input id="city" v-model="organization.city" class="form-input" placeholder="New York">
                </div>
                <div class="form-group">
                  <label for="state">State</label>
                  <input id="state" v-model="organization.state" class="form-input" placeholder="NY">
                </div>
                <div class="form-group">
                  <label for="postal">Postal code</label>
                  <input id="postal" v-model="organization.postal_code" class="form-input" placeholder="10001">
                </div>
              </div>
            </fieldset>

            <div class="form-actions">
              <button type="submit" class="btn btn-primary" :disabled="updating">
                <i v-if="updating" class="fas fa-spinner fa-spin"></i>
                <i v-else class="fas fa-save"></i>
                {{ updating ? 'Saving…' : 'Save changes' }}
              </button>
              <button type="button" class="btn btn-secondary" :disabled="loading" @click="loadOrganization">
                <i class="fas fa-undo"></i>
                Reset
              </button>
            </div>
          </form>
        </section>

        <!-- Compact metrics — content-sized, not stretched -->
        <aside class="metrics-panel">
          <h3 class="metrics-title">At a glance</h3>
          <dl class="metrics-list">
            <div class="metric-row">
              <dt>Active users</dt>
              <dd>{{ organization.user_count || 0 }}</dd>
            </div>
            <div class="metric-row">
              <dt>Subscription</dt>
              <dd>{{ organization.subscription_tier || '—' }}</dd>
            </div>
            <div class="metric-row">
              <dt>Status</dt>
              <dd>
                <span class="status-chip" :class="organization.is_active ? 'is-active' : 'is-inactive'">
                  {{ organization.is_active ? 'Active' : 'Inactive' }}
                </span>
              </dd>
            </div>
          </dl>
        </aside>
      </div>

      <!-- HIPAA / BAA -->
      <section class="settings-card edge-card" :class="baaEdgeClass">
        <div class="edge-card-inner">
          <div class="card-header card-header--compact">
            <i class="fas fa-shield-alt" aria-hidden="true"></i>
            <div>
              <h2 class="card-title">HIPAA / Business Associate Agreement</h2>
              <p class="card-desc">
                Required when this organization handles Protected Health Information (PHI).
              </p>
            </div>
          </div>
          <div class="baa-body">
            <div class="baa-status-line">
              <span class="baa-label">Status</span>
              <strong>{{ baaStatusText }}</strong>
            </div>
            <p v-if="baaStatus?.baa_status === 'SIGNED'" class="baa-detail">
              Signed by {{ baaStatus.baa_signatory_name }} on {{ formatDate(baaStatus.baa_signed_at) }}
            </p>
            <p v-else-if="baaStatus?.hipaa_compliance_required" class="baa-detail">
              A signed BAA is required before processing PHI in this org.
            </p>
            <p v-else class="baa-detail">
              Sign a BAA if you store or review healthcare documents containing PHI.
            </p>
            <router-link
              to="/settings/baa"
              class="btn"
              :class="baaStatus?.baa_status === 'SIGNED' ? 'btn-secondary' : 'btn-primary'"
            >
              <i :class="baaStatus?.baa_status === 'SIGNED' ? 'fas fa-eye' : 'fas fa-file-signature'"></i>
              {{ baaStatus?.baa_status === 'SIGNED' ? 'View BAA' : 'Sign BAA' }}
            </router-link>
          </div>
        </div>
      </section>

      <!-- Email branding purpose cell -->
      <section class="settings-card purpose-cell">
        <div class="purpose-cell-header">
          <div class="card-header card-header--compact">
            <i class="fas fa-envelope-open-text" aria-hidden="true"></i>
            <div>
              <h2 class="card-title">Email branding &amp; templates</h2>
              <p class="card-desc">
                Control the From address, subject lines, and wrapped header/footer on COI upload-request emails sent from this org.
              </p>
            </div>
          </div>
          <router-link to="/settings/email-branding" class="btn btn-primary">
            <i class="fas fa-pen"></i>
            Edit email branding
          </router-link>
        </div>
        <ul class="feature-checklist">
          <li><i class="fas fa-check"></i> From address, display name, reply-to</li>
          <li><i class="fas fa-check"></i> Subject lines (basic, AI, gap follow-up)</li>
          <li><i class="fas fa-check"></i> Header / footer template and sign-off</li>
          <li><i class="fas fa-check"></i> AI tone &amp; style guide</li>
        </ul>
      </section>

      <!-- Developer agreements purpose cell -->
      <section class="settings-card purpose-cell">
        <div class="purpose-cell-header">
          <div class="card-header card-header--compact">
            <i class="fas fa-file-contract" aria-hidden="true"></i>
            <div>
              <h2 class="card-title">Developer agreements</h2>
              <p class="card-desc">
                Send consulting and IP-assignment agreements for e-signature; PDF copies go to both parties.
              </p>
            </div>
          </div>
          <router-link to="/settings/developer-agreements" class="btn btn-primary">
            <i class="fas fa-paper-plane"></i>
            Manage agreements
          </router-link>
        </div>
      </section>
    </template>

    <div v-if="notification" class="toast" :class="`toast--${notification.type}`" role="status">
      <i :class="getNotificationIcon(notification.type)"></i>
      {{ notification.message }}
    </div>
  </div>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'OrganizationSettings',
  data() {
    return {
      loading: false,
      updating: false,
      organization: {
        name: '',
        description: '',
        industry: '',
        primary_contact_email: '',
        primary_contact_phone: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        postal_code: '',
        country: 'US',
        subscription_tier: '',
        user_count: 0,
        is_active: true
      },
      baaStatus: null,
      notification: null
    }
  },

  computed: {
    baaEdgeClass() {
      if (!this.baaStatus) return 'edge-neutral';
      if (this.baaStatus.baa_status === 'SIGNED') return 'edge-ok';
      if (this.baaStatus.hipaa_compliance_required) return 'edge-warn';
      return 'edge-neutral';
    },
    baaStatusText() {
      if (!this.baaStatus) return 'Loading…';
      if (this.baaStatus.baa_status === 'SIGNED') return 'Active';
      if (this.baaStatus.baa_status === 'PENDING') return 'Pending signature';
      if (this.baaStatus.baa_status === 'SENT') return 'Awaiting signature';
      if (this.baaStatus.hipaa_compliance_required) return 'Required';
      return 'Not required';
    }
  },

  async mounted() {
    await Promise.all([
      this.loadOrganization(),
      this.loadBaaStatus()
    ]);
  },

  methods: {
    async loadBaaStatus() {
      try {
        const response = await api.get('/users/baa/status/');
        this.baaStatus = response.data;
      } catch (error) {
        console.error('Failed to load BAA status:', error);
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return 'N/A';
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },

    async loadOrganization() {
      this.loading = true;
      try {
        const response = await api.get('/users/organization/info/');
        this.organization = { ...this.organization, ...response.data };
      } catch (error) {
        this.showNotification('error', 'Failed to load organization information');
        console.error('Load organization error:', error);
      } finally {
        this.loading = false;
      }
    },

    async updateOrganization() {
      this.updating = true;
      try {
        await api.put('/users/organization/info/', this.organization);
        this.showNotification('success', 'Organization updated successfully');
      } catch (error) {
        this.showNotification('error', error.response?.data?.detail || 'Failed to update organization');
      } finally {
        this.updating = false;
      }
    },

    showNotification(type, message) {
      this.notification = { type, message };
      setTimeout(() => {
        this.notification = null;
      }, 5000);
    },

    getNotificationIcon(type) {
      const icons = {
        success: 'fas fa-check-circle',
        error: 'fas fa-exclamation-circle',
        warning: 'fas fa-exclamation-triangle',
        info: 'fas fa-info-circle'
      };
      return icons[type] || icons.info;
    }
  }
}
</script>

<style scoped>
.org-settings {
  max-width: 960px;
}

.page-header {
  margin-bottom: 1.5rem;
}

.page-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--p-ink, #1e293b);
  margin: 0 0 0.35rem;
}

.page-subtitle {
  margin: 0;
  font-size: 0.9rem;
  color: var(--p-ink-muted, #64748b);
  line-height: 1.5;
  max-width: 42rem;
}

.page-subtitle strong {
  color: var(--p-ink, #1e293b);
  font-weight: 600;
}

.loading-state {
  padding: 2rem;
  text-align: center;
  color: var(--p-ink-muted, #64748b);
}

.org-layout {
  display: grid;
  grid-template-columns: 1fr 200px;
  gap: 1.25rem;
  align-items: start;
  margin-bottom: 1.25rem;
}

.settings-card {
  background: var(--p-surface-elevated, #fff);
  border: 1px solid var(--p-border, #e2e8f0);
  border-radius: var(--p-radius, 0.75rem);
  padding: 1.25rem 1.35rem;
  margin-bottom: 1.25rem;
}

.org-layout .settings-card {
  margin-bottom: 0;
}

.card-header {
  display: flex;
  gap: 0.85rem;
  align-items: flex-start;
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--p-border, #e2e8f0);
}

.card-header--compact {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
  flex: 1;
  min-width: 0;
}

.card-header > i {
  color: var(--p-accent, #1e3a5f);
  font-size: 1.15rem;
  margin-top: 0.15rem;
  flex-shrink: 0;
}

.card-title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--p-ink, #1e293b);
  margin: 0 0 0.25rem;
}

.card-desc {
  margin: 0;
  font-size: 0.85rem;
  color: var(--p-ink-muted, #64748b);
  line-height: 1.45;
}

.org-form {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-row {
  display: flex;
  gap: 1rem;
}

.form-row--3 .form-group {
  flex: 1;
  min-width: 0;
}

.form-group {
  flex: 1;
  margin-bottom: 0.65rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.35rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--p-ink, #1e293b);
}

.required {
  color: var(--p-danger, #ef4444);
}

.form-input {
  width: 100%;
  padding: 0.55rem 0.75rem;
  border: 1px solid var(--p-border-strong, #cbd5e1);
  border-radius: var(--p-radius-sm, 0.5rem);
  font-size: 0.9rem;
  color: var(--p-ink, #1e293b);
  background: var(--p-surface-muted, #f8fafc);
  box-sizing: border-box;
  transition: border-color var(--p-transition, 180ms ease), box-shadow var(--p-transition, 180ms ease);
}

.form-input:focus {
  outline: none;
  border-color: var(--p-accent, #1e3a5f);
  background: #fff;
  box-shadow: var(--p-focus-ring, 0 0 0 3px color-mix(in srgb, #1e3a5f 25%, transparent));
}

.address-fieldset {
  border: none;
  margin: 0.75rem 0 0;
  padding: 0.75rem 0 0;
  border-top: 1px solid var(--p-border, #e2e8f0);
}

.address-fieldset legend {
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--p-ink-muted, #64748b);
  margin-bottom: 0.65rem;
  padding: 0;
}

.form-actions {
  display: flex;
  gap: 0.65rem;
  flex-wrap: wrap;
  margin-top: 0.75rem;
  padding-top: 1rem;
  border-top: 1px solid var(--p-border, #e2e8f0);
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.9rem;
  border-radius: var(--p-radius-sm, 0.5rem);
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  text-decoration: none;
  transition: background var(--p-transition, 180ms ease), border-color var(--p-transition, 180ms ease);
}

.btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.btn-primary {
  background: var(--p-accent, #1e3a5f);
  color: var(--p-accent-contrast, #fff);
  border-color: var(--p-accent, #1e3a5f);
}

.btn-primary:hover:not(:disabled) {
  background: var(--p-accent-hover, #162b46);
}

.btn-secondary {
  background: var(--p-surface-elevated, #fff);
  color: var(--p-accent, #1e3a5f);
  border-color: var(--p-border-strong, #cbd5e1);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--p-accent-subtle, #eef2f8);
}

/* Compact metrics panel */
.metrics-panel {
  background: var(--p-surface-muted, #f8fafc);
  border: 1px solid var(--p-border, #e2e8f0);
  border-radius: var(--p-radius, 0.75rem);
  padding: 1rem 1.1rem;
}

.metrics-title {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--p-ink-muted, #64748b);
  margin: 0 0 0.75rem;
}

.metrics-list {
  margin: 0;
}

.metric-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
  padding: 0.45rem 0;
  border-bottom: 1px solid var(--p-border, #e2e8f0);
}

.metric-row:last-child {
  border-bottom: none;
}

.metric-row dt {
  font-size: 0.8rem;
  color: var(--p-ink-muted, #64748b);
  font-weight: 500;
}

.metric-row dd {
  margin: 0;
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--p-ink, #1e293b);
  text-align: right;
}

.status-chip {
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.15rem 0.45rem;
  border-radius: 4px;
}

.status-chip.is-active {
  background: #dcfce7;
  color: #166534;
}

.status-chip.is-inactive {
  background: #f1f5f9;
  color: #64748b;
}

/* Edge status cards (spoke-entity-cards pattern) */
.edge-card {
  border-left-width: 4px;
  padding-left: calc(1.35rem - 3px);
}

.edge-ok {
  border-left-color: #16a34a;
}

.edge-warn {
  border-left-color: #d97706;
}

.edge-neutral {
  border-left-color: var(--p-border-strong, #cbd5e1);
}

.baa-body {
  margin-top: 1rem;
}

.baa-status-line {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
}

.baa-label {
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--p-ink-muted, #64748b);
}

.baa-detail {
  margin: 0 0 0.85rem;
  font-size: 0.85rem;
  color: var(--p-ink-muted, #64748b);
  line-height: 1.45;
}

/* Purpose cells */
.purpose-cell-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 0.75rem;
}

.feature-checklist {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.35rem 1rem;
}

.feature-checklist li {
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;
  font-size: 0.82rem;
  color: var(--p-ink-muted, #64748b);
}

.feature-checklist i {
  color: var(--p-success, #16a34a);
  margin-top: 0.15rem;
  font-size: 0.75rem;
}

.toast {
  position: fixed;
  top: 1.25rem;
  right: 1.25rem;
  padding: 0.75rem 1rem;
  border-radius: var(--p-radius-sm, 0.5rem);
  font-size: 0.88rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  z-index: 1100;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.toast--success {
  background: #dcfce7;
  color: #166534;
  border: 1px solid #bbf7d0;
}

.toast--error {
  background: #fef2f2;
  color: #991b1b;
  border: 1px solid #fecaca;
}

.toast--warning {
  background: #fffbeb;
  color: #92400e;
  border: 1px solid #fde68a;
}

.toast--info {
  background: #eff6ff;
  color: #1e40af;
  border: 1px solid #bfdbfe;
}

@media (max-width: 768px) {
  .org-layout {
    grid-template-columns: 1fr;
  }

  .form-row,
  .form-row--3 {
    flex-direction: column;
    gap: 0;
  }

  .purpose-cell-header {
    flex-direction: column;
  }

  .purpose-cell-header .btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
